type CardStatusLike = {
    card_approval?: string | null
}

type CardBlockedRowLike = {
    created_at?: string | null
}

export type CardBlockedResolution<TStatus extends object, TBlocked extends object> = {
    normalizedCardStatus: TStatus | null
    normalizedBlockedRow: TBlocked | null
    cardWasBlocked: boolean
    cardCurrentlyBlocked: boolean
    hasInvalidDates: boolean
}

export function normalizeStatusRow<T extends object>(row?: T | null): T | null {
    // Empty rows mean there is no data.
    if (!row || Object.keys(row).length === 0) {
        return null
    }

    return row
}

export function resolveCardBlockedStatus<
    TStatus extends CardStatusLike & object,
    TBlocked extends CardBlockedRowLike & object
>(cardStatus?: TStatus | null, recentCardBlockedRow?: TBlocked | null): CardBlockedResolution<TStatus, TBlocked> {
    const normalizedCardStatus = normalizeStatusRow(cardStatus)
    const normalizedBlockedRow = normalizeStatusRow(recentCardBlockedRow)

    // No blocked row means the card is not blocked now and was never blocked.
    if (!normalizedBlockedRow) {
        return {
            normalizedCardStatus,
            normalizedBlockedRow,
            cardWasBlocked: false,
            cardCurrentlyBlocked: false,
            hasInvalidDates: false
        }
    }
    // If we got here, there is a blocked row.
    // Check the approval data for this blocked row.
    if (normalizedCardStatus) {
        // No approval date means the card is still blocked.
        if (normalizedCardStatus.card_approval == null) {
            return {
                normalizedCardStatus,
                normalizedBlockedRow,
                cardWasBlocked: false,
                cardCurrentlyBlocked: true,
                hasInvalidDates: false
            }
        }
        // If we got here, there is an approval date.
        // Compare the approval date to the block date.
        if (normalizedCardStatus.card_approval !== null) {
            const approvalDate = new Date(normalizedCardStatus.card_approval || '')
            const newBlockDate = new Date(normalizedBlockedRow.created_at || '')

            // Invalid dates are treated as still blocked.
            if (isNaN(approvalDate.getTime()) || isNaN(newBlockDate.getTime())) {
                return {
                    normalizedCardStatus,
                    normalizedBlockedRow,
                    cardWasBlocked: false,
                    cardCurrentlyBlocked: true,
                    hasInvalidDates: true
                }
            }
            // If we got here, both dates are valid.
            // If approval is after the block, the card was released.
            // Otherwise, the card is still blocked.
            return {
                normalizedCardStatus,
                normalizedBlockedRow,
                cardWasBlocked: approvalDate > newBlockDate,
                cardCurrentlyBlocked: approvalDate <= newBlockDate,
                hasInvalidDates: false
            }
        }
    }

    // Any other case means the card is not blocked.
    return {
        normalizedCardStatus,
        normalizedBlockedRow,
        cardWasBlocked: false,
        cardCurrentlyBlocked: false,
        hasInvalidDates: false
    }
}
